
import { Vue, Component } from 'vue-property-decorator';
import QueryStringTopPanel from '@/features/query-string/components/QueryStringTopPanel.vue';
import QueryStringDocs from '@/features/query-string/components/QueryStringDocs.vue';
import { createShipmentQueryParams, defaultInProgressData, getEstimateQueryParams } from '@/features/common/raw';
import { parseQueryString } from '@/features/query-string/helpers';
import { getInProgressData } from '@/features/shipment/services/shipment.services';
import { ShipFrom, ShipTo, SignatureType } from '@/features/shipment/types';
import { getShipToAddressInPlainText } from '@/features/shipment/helpers/createShipment';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { getOrigin } from '@/features/common/helpers';
import QueryStringDebug from '@/features/query-string/components/debug/QueryStringDebug.vue';

export interface QueryStringValue {
  parameter: string,
  required: boolean,
  requiredForInternational?: boolean,
  defaultValue?: string,
}

export interface EndpointItem {
  name: string,
  title: string,
  endpoint: string,
  sampleQueryString: string,
  values: QueryStringValue[]
}

@Component({
  components: {
    QueryStringTopPanel,
    QueryStringDocs,
    QueryStringDebug,
  },
})
export default class QueryString extends Vue {
  private estimateEndpoint = `${getOrigin()}/connect/query-string/get-estimate`;

  private createShipmentEndpoint = `${getOrigin()}/connect/query-string/create-shipment`;

  private endpoints: EndpointItem[] = [
    {
      title: this.$t('estimate.getEstimateTitle') as string,
      name: 'getEstimate',
      endpoint: this.estimateEndpoint,
      sampleQueryString: `${this.estimateEndpoint}?FromCC=CA&FromPC=k1k1k1&FromCity=Ottawa&ToCC=US&ToPC=90210&ToCity=Beverly%20Hills&PT1=MyPackage&Weight1=5&PT2=MyPackage&Weight2=6&L2=4&W2=6&H2=8`,
      values: getEstimateQueryParams,
    },
    {
      title: this.$t('shipment.createShipment') as string,
      name: 'createShipment',
      endpoint: this.createShipmentEndpoint,
      sampleQueryString: `${this.createShipmentEndpoint}?FromCC=CA&FromPC=k1k1k1&FromTel=555-123-4567&FromEmail=john@example.com&FromCity=Ottawa&FromProv=ON&FromCompany=From%20Company&FromContact=From%20Contact&FromAddr1=1st%20main%20street&ToCC=US&ToPC=90210&ToCompany=To%20Company&ToContact=To%20Contact&ToTel=555-123-4567&ToEmail=alex@example.com&ToCity=Beverly&ToProv=CA%20Hills&ToAddr1=1%20main%20street&ToAddr2=Suite%20555&Ref=Reference%20value&Comments=Delivery%20Comments&PT1=MyPackage&Weight1=5&PT2=MyPackage&Weight2=6&L2=4&W2=6&H2=8&Signature=signature&ToResidential=1&FromResidential=1`,
      values: createShipmentQueryParams,
    },
  ]

  get isApplyState(): boolean {
    return this.$route.name !== 'query-string';
  }

  get applyToEstimate(): boolean {
    return this.$route.name === 'query-string-estimate';
  }

  get isDebug(): boolean {
    return parseQueryString(this.$route.query).isDebug;
  }

  private async applyQueryString(): Promise<void> {
    if (this.isDebug) {
      return;
    }

    const {
      fromAddress, toAddress, extras, packages, isDocumentsOnly, emailNotifications,
    } = parseQueryString(this.$route.query, this.applyToEstimate);

    const inProgress = await getInProgressData();
    let toAddressNotChanged = true;
    let newInProgress = { ...inProgress };

    if (!inProgress) {
      newInProgress = { ...defaultInProgressData };
    } else {
      toAddressNotChanged = !inProgress || Object.values(inProgress?.toAddress as ShipFrom)
        .every((v) => v === false || v.length === 0);
    }

    const applyData = async () => {
      if (this.applyToEstimate) {
        if (fromAddress) {
          this.$store.commit('patchEstimateData', { key: 'fromAddress', value: fromAddress });
        }

        if (toAddress) {
          this.$store.commit('patchEstimateData', { key: 'toAddress', value: toAddress });
        }

        if (packages.length) {
          this.$store.commit('patchEstimateData', { key: 'packages', value: packages });
        }

        if (extras.signature) {
          this.$store.commit('patchEstimateData', { key: 'signatureOptions', value: extras.signature as SignatureType });
        }

        this.$store.commit('patchEstimateData', { key: 'isDocumentsOnly', value: isDocumentsOnly });

        this.$router.push({ name: 'estimate' });
        return;
      }

      this.$store.commit('setLoading', true);

      if (fromAddress) {
        newInProgress.fromAddress = fromAddress as ShipFrom;
      }

      if (toAddress) {
        newInProgress.toAddress = toAddress as ShipTo;
      }

      if (extras.comments) {
        newInProgress.comments = extras.comments;
      }

      if (extras.reference) {
        newInProgress.references = [extras.reference];
      }

      if (extras.signature) {
        newInProgress.signatureOptions = extras.signature as SignatureType;
      }

      if (packages.length) {
        newInProgress.packages = packages;
      }

      newInProgress.documentsOnly = isDocumentsOnly;

      if (emailNotifications) {
        newInProgress.emailNotifications = [{
          email: {
            emailAddress: emailNotifications,
          },
          delivery: true,
          exception: false,
          ship: false,
        }];
      }

      this.$store.commit('setInProgress', newInProgress);
      await this.$store.dispatch('syncInProgressData');

      this.$router.push({ name: 'shipment-create' });
      this.$store.commit('setLoading', false);
    };

    if (toAddressNotChanged || this.applyToEstimate) {
      applyData();
    } else {
      eventBus.$once(eventNames.CONFIRMATION_DIALOG, applyData);

      eventBus.$once(eventNames.CONFIRMATION_DIALOG_REJECTED, () => {
        this.$router.push({ name: 'shipment-create' });
      });

      this.$store.commit('toggleConfirmDialog', {
        show: true,
        content: this.$t('shipment.editShipmentPrompt', {
          shipTo: getShipToAddressInPlainText(inProgress?.toAddress as ShipTo),
        }) as string,
        btnText: this.$t('buttons.ok') as string,
        title: this.$t('alertDialog.currentlyWorkingOn') as string,
      });
    }
  }

  private created(): void {
    if (this.isApplyState) {
      this.applyQueryString();
    }
  }
}
